import React, { useEffect, useMemo, useRef, useState } from "react";
import GuidedTour from "../../components/GuidedTour";
import { tourStepsTypingSimulator } from "./tourStepsTypingSimulator";
import { useNavigate } from "react-router-dom";
import BubbleText from "../../components/BubbleText";
import GamePageToolbar from "../../components/GamePageToolbar";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Collection,
  GamePair,
  deleteCollection,
  getGameCollections,
  saveGameCollection,
} from "../../api";

const SAMPLE_PASSAGES = [
  {
    id: "short",
    title: "Short practice",
    text: "The quick brown fox jumps over the lazy dog.",
  },
  {
    id: "steady",
    title: "Steady rhythm",
    text: "Practice makes progress. Type each letter carefully and keep a steady rhythm as you go.",
  },
  {
    id: "classroom",
    title: "Classroom",
    text: "Reading helps you hear the words. Typing helps you see each letter, space, and punctuation mark.",
  },
] as const;

const HIDDEN_SAMPLE_PASSAGES_KEY = "typingSimulatorHiddenSamples";

const readHiddenSampleIds = (): string[] => {
  try {
    const stored = localStorage.getItem(HIDDEN_SAMPLE_PASSAGES_KEY);
    return stored ? (JSON.parse(stored) as string[]) : [];
  } catch {
    return [];
  }
};

const parsePassageText = (description: string): string => {
  try {
    let parsed: unknown = JSON.parse(description);
    if (typeof parsed === "string") {
      parsed = JSON.parse(parsed);
    }
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return "";
    }
    const first = parsed[0] as GamePair;
    return typeof first.imageUrl === "string" ? first.imageUrl : "";
  } catch {
    return "";
  }
};

const TypingSimulator: React.FC = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [isGameStarted, setIsGameStarted] = useState(false);
  const [isGameCompleted, setIsGameCompleted] = useState(false);
  const [hiddenSampleIds, setHiddenSampleIds] = useState<string[]>(
    readHiddenSampleIds,
  );
  const [selectedPassageId, setSelectedPassageId] = useState<string>(() => {
    const hidden = readHiddenSampleIds();
    const visible = SAMPLE_PASSAGES.find(
      (passage) => !hidden.includes(passage.id),
    );
    return visible?.id ?? "custom";
  });
  const [customText, setCustomText] = useState("");
  const [passageName, setPassageName] = useState("");
  const [savedPassages, setSavedPassages] = useState<Collection[]>([]);
  const [prompt, setPrompt] = useState("");
  const [typed, setTyped] = useState("");
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [currentTourStep, setCurrentTourStep] = useState(0);

  useEffect(() => {
    const tourCompleted = localStorage.getItem("tourCompleted_typingSimulator");
    if (!tourCompleted) {
      setIsTourRunning(true);
    }
  }, []);

  useEffect(() => {
    if (!isGameStarted || isGameCompleted || startedAt === null) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, 100);

    return () => window.clearInterval(intervalId);
  }, [isGameStarted, isGameCompleted, startedAt]);

  useEffect(() => {
    if (isGameStarted) {
      inputRef.current?.focus();
    }
  }, [isGameStarted]);

  const loadSavedPassages = async () => {
    try {
      const collections = await getGameCollections(
        "typing_simulator",
        getAccessTokenSilently,
      );
      setSavedPassages(collections ?? []);
    } catch (error) {
      console.error("Failed to load saved passages:", error);
    }
  };

  useEffect(() => {
    loadSavedPassages();
  }, [getAccessTokenSilently]);

  const selectedSavedPassage = savedPassages.find(
    (collection) => `saved-${collection.collection_id}` === selectedPassageId,
  );
  const visibleSamplePassages = SAMPLE_PASSAGES.filter(
    (passage) => !hiddenSampleIds.includes(passage.id),
  );
  const selectedSamplePassage = visibleSamplePassages.find(
    (passage) => passage.id === selectedPassageId,
  );

  const selectedPrompt = (() => {
    if (selectedPassageId === "custom") {
      return customText.trim();
    }
    if (selectedSavedPassage) {
      return parsePassageText(selectedSavedPassage.description);
    }
    return selectedSamplePassage?.text ?? "";
  })();

  const persistHiddenSampleIds = (nextIds: string[]) => {
    setHiddenSampleIds(nextIds);
    localStorage.setItem(HIDDEN_SAMPLE_PASSAGES_KEY, JSON.stringify(nextIds));
  };

  const selectFallbackPassage = (nextHiddenIds: string[]) => {
    const nextVisible = SAMPLE_PASSAGES.filter(
      (passage) => !nextHiddenIds.includes(passage.id),
    );
    if (nextVisible.length > 0) {
      setSelectedPassageId(nextVisible[0].id);
      return;
    }
    if (savedPassages.length > 0 && savedPassages[0].collection_id != null) {
      setSelectedPassageId(`saved-${savedPassages[0].collection_id}`);
      return;
    }
    setSelectedPassageId("custom");
  };

  const handleHideSamplePassage = (passageId: string) => {
    if (
      !window.confirm(
        "Remove this example from the list? You can restore examples later.",
      )
    ) {
      return;
    }
    const nextHiddenIds = hiddenSampleIds.includes(passageId)
      ? hiddenSampleIds
      : [...hiddenSampleIds, passageId];
    persistHiddenSampleIds(nextHiddenIds);
    if (selectedPassageId === passageId) {
      selectFallbackPassage(nextHiddenIds);
    }
  };

  const handleRestoreSamplePassages = () => {
    persistHiddenSampleIds([]);
  };

  const stats = useMemo(() => {
    let correctCount = 0;
    const comparedLength = Math.min(typed.length, prompt.length);

    for (let index = 0; index < comparedLength; index += 1) {
      if (typed[index] === prompt[index]) {
        correctCount += 1;
      }
    }

    const minutes = Math.max(elapsedMs / 60000, 1 / 60);
    const wpm = typed.length === 0 ? 0 : typed.length / 5 / minutes;
    const accuracy =
      typed.length === 0 ? 100 : (correctCount / typed.length) * 100;

    return {
      correctCount,
      wpm,
      accuracy,
    };
  }, [elapsedMs, prompt, typed]);

  const resetTypingState = (nextPrompt = prompt) => {
    setPrompt(nextPrompt);
    setTyped("");
    setStartedAt(null);
    setElapsedMs(0);
    setIsGameCompleted(false);
  };

  const startGame = () => {
    if (!selectedPrompt) {
      return;
    }

    resetTypingState(selectedPrompt);
    setIsGameStarted(true);
  };

  const handleSavePassage = async () => {
    if (!customText.trim()) {
      alert("Write a passage before saving.");
      return;
    }
    if (!passageName.trim()) {
      alert("Please enter a name for this passage.");
      return;
    }

    try {
      await saveGameCollection(
        passageName.trim(),
        [{ word: passageName.trim(), imageUrl: customText.trim() }],
        "typing_simulator",
        getAccessTokenSilently,
      );
      setPassageName("");
      await loadSavedPassages();
      alert("Passage saved. You can pick it from the list later.");
    } catch (error) {
      console.error("Failed to save passage:", error);
      alert("Failed to save passage. Please try again.");
    }
  };

  const handleDeleteSavedPassage = async (collectionId: number) => {
    if (!window.confirm("Remove this saved passage?")) {
      return;
    }
    try {
      await deleteCollection(collectionId, getAccessTokenSilently);
      if (selectedPassageId === `saved-${collectionId}`) {
        const remaining = savedPassages.filter(
          (collection) => collection.collection_id !== collectionId,
        );
        if (visibleSamplePassages.length > 0) {
          setSelectedPassageId(visibleSamplePassages[0].id);
        } else if (
          remaining.length > 0 &&
          remaining[0].collection_id != null
        ) {
          setSelectedPassageId(`saved-${remaining[0].collection_id}`);
        } else {
          setSelectedPassageId("custom");
        }
      }
      await loadSavedPassages();
    } catch (error) {
      console.error("Failed to delete passage:", error);
      alert("Failed to remove passage. Please try again.");
    }
  };

  const handleTypedChange = (value: string) => {
    if (isGameCompleted) {
      return;
    }

    const nextTyped = value.slice(0, prompt.length);
    const nextStartedAt =
      startedAt ?? (nextTyped.length > 0 ? Date.now() : null);

    if (startedAt === null && nextStartedAt !== null) {
      setStartedAt(nextStartedAt);
    }

    setTyped(nextTyped);

    if (nextTyped.length === prompt.length && nextStartedAt !== null) {
      setIsGameCompleted(true);
      setElapsedMs(Date.now() - nextStartedAt);
    }
  };

  const handleBack = () => {
    navigate("/games");
  };

  const goBackToSetup = () => {
    setIsGameStarted(false);
    resetTypingState();
  };

  const handleTourComplete = () => {
    setIsTourRunning(false);
    localStorage.setItem("tourCompleted_typingSimulator", "true");
  };

  const formatTime = (milliseconds: number) => {
    const totalSeconds = milliseconds / 1000;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toFixed(1).padStart(4, "0")}`;
  };

  const canStart = selectedPrompt.length > 0;

  return (
    <div className={`page-container text-theme-text`}>
      <GamePageToolbar onBack={handleBack}>
        {isGameStarted && (
          <>
            <button
              type="button"
              onClick={() => {
                resetTypingState();
                inputRef.current?.focus();
              }}
              className="reset-button max-w-[300px] cursor-pointer rounded border border-black bg-yellow-500 px-3 py-2 text-base font-bold uppercase text-black transition duration-300 hover:scale-105 hover:bg-yellow-600 active:scale-95 active:bg-yellow-700"
            >
              Reset Game
            </button>
            <button
              type="button"
              onClick={goBackToSetup}
              className="back-to-setup-button max-w-[300px] cursor-pointer rounded border border-black bg-blue-500 px-3 py-2 text-base font-bold uppercase text-white transition duration-300 hover:bg-blue-600"
            >
              Back to Setup
            </button>
          </>
        )}
      </GamePageToolbar>
      <h1 className="mb-8 text-3xl font-bold">
        <BubbleText>Typing Simulator</BubbleText>
      </h1>

      {!isGameStarted ? (
        <div className="game-instructions flex w-full max-w-2xl flex-col items-center">
          <p className="inherit mb-4 text-center">
            A passage will appear on the screen. Type it as you see it.
          </p>
          <label htmlFor="passageSelect" className="mb-2 block font-semibold">
            Choose a passage
          </label>
          <select
            id="passageSelect"
            value={selectedPassageId}
            onChange={(event) => setSelectedPassageId(event.target.value)}
            className="mb-4 w-full rounded border border-black bg-white p-2 text-black"
          >
            {visibleSamplePassages.map((passage) => (
              <option key={passage.id} value={passage.id}>
                {passage.title}
              </option>
            ))}
            {savedPassages.map((collection) => (
              <option
                key={collection.collection_id}
                value={`saved-${collection.collection_id}`}
              >
                {collection.name}
              </option>
            ))}
            <option value="custom">Write your own</option>
          </select>

          {selectedPassageId === "custom" ? (
            <>
              <textarea
                value={customText}
                onChange={(event) => setCustomText(event.target.value)}
                placeholder="Type or paste the text you want to practice."
                className="mb-3 min-h-[140px] w-full rounded border border-black bg-white p-3 text-black"
              />
              <div className="mb-4 flex w-full flex-col gap-2 sm:flex-row">
                <input
                  type="text"
                  value={passageName}
                  onChange={(event) => setPassageName(event.target.value)}
                  placeholder="Name this passage"
                  className="w-full rounded border border-black bg-white p-2 text-black"
                />
                <button
                  type="button"
                  onClick={handleSavePassage}
                  className="rounded border border-black bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
                >
                  Save Passage
                </button>
              </div>
            </>
          ) : (
            <div className="mb-4 w-full">
              <p className="rounded border border-black bg-white p-3 text-left text-black">
                {selectedPrompt}
              </p>
              {selectedSavedPassage && (
                <button
                  type="button"
                  onClick={() =>
                    handleDeleteSavedPassage(selectedSavedPassage.collection_id)
                  }
                  className="mt-2 rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
                >
                  Remove Saved Passage
                </button>
              )}
              {selectedSamplePassage && (
                <button
                  type="button"
                  onClick={() =>
                    handleHideSamplePassage(selectedSamplePassage.id)
                  }
                  className="mt-2 rounded bg-red-500 px-4 py-2 text-white hover:bg-red-600"
                >
                  Remove Example
                </button>
              )}
            </div>
          )}
          {hiddenSampleIds.length > 0 && (
            <button
              type="button"
              onClick={handleRestoreSamplePassages}
              className="mb-4 text-sm underline"
            >
              Restore example passages
            </button>
          )}

          <button
            type="button"
            onClick={startGame}
            disabled={!canStart}
            className={`start-button mt-2 max-w-[300px] rounded border border-black bg-green-500 p-2.5 text-base font-bold uppercase text-white transition duration-300 hover:bg-green-600 ${
              canStart ? "cursor-pointer" : "cursor-not-allowed opacity-50"
            }`}
          >
            Start Game
          </button>
        </div>
      ) : (
        <div className="typing-game flex w-full max-w-3xl flex-col items-center">
          <div className="mb-4 grid w-full grid-cols-1 gap-4 text-center sm:grid-cols-3">
            <div>
              <p className="text-sm uppercase">WPM</p>
              <p className="text-2xl font-bold">{Math.round(stats.wpm)}</p>
            </div>
            <div>
              <p className="text-sm uppercase">Accuracy</p>
              <p className="text-2xl font-bold">{Math.round(stats.accuracy)}%</p>
            </div>
            <div>
              <p className="text-sm uppercase">Time</p>
              <p className="text-2xl font-bold">{formatTime(elapsedMs)}</p>
            </div>
          </div>

          <div
            className="typing-prompt mb-4 max-h-[40vh] w-full cursor-text overflow-y-auto rounded-lg border border-black bg-white p-4 text-left text-base leading-relaxed text-black sm:max-h-none sm:text-xl"
            onClick={() => inputRef.current?.focus()}
          >
            {prompt.split("").map((character, index) => {
              let className = "text-gray-500";
              if (index < typed.length) {
                className =
                  typed[index] === character
                    ? "text-green-700"
                    : "bg-red-200 text-red-700";
              } else if (index === typed.length && !isGameCompleted) {
                className =
                  "bg-blue-200 text-black underline decoration-blue-500";
              }

              return (
                <span key={`${character}-${index}`} className={className}>
                  {character}
                </span>
              );
            })}
          </div>

          <label htmlFor="typingInput" className="mb-2 font-semibold">
            Type here
          </label>
          <textarea
            id="typingInput"
            ref={inputRef}
            value={typed}
            onChange={(event) => handleTypedChange(event.target.value)}
            onPaste={(event) => event.preventDefault()}
            disabled={isGameCompleted}
            className="min-h-[120px] w-full rounded border border-black bg-white p-3 text-lg text-black"
            placeholder="Start typing the passage above."
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />

          {isGameCompleted && (
            <p className="mt-6 text-xl font-semibold">
              Finished! {Math.round(stats.wpm)} WPM at{" "}
              {Math.round(stats.accuracy)}% accuracy.
            </p>
          )}
        </div>
      )}

      <GuidedTour
        steps={tourStepsTypingSimulator()}
        isRunning={isTourRunning}
        onComplete={handleTourComplete}
        currentStep={currentTourStep}
        onStepChange={setCurrentTourStep}
        tourName="typingSimulator"
      />
    </div>
  );
};

export default TypingSimulator;
